import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconPlus } from '@tabler/icons-react';
import { Button, Card, LoadingOverlay, Paper } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import DataView from './components/DataView';
import { EquipmentType } from '../../../../models/core/equipment.type';
import { Filter, FilterData } from './components/Filter';
import PageContent from '../../../../components/core/PageContent/PageContent';
import equipmentsEquipment from '../../../../services/core/equipment.service';
import { Feature, SessionStorageKey } from '../../../../utils/constants.utils';
import { Permission } from '../../../../models/core/departments.type';

function EquipmentSearch() {
  const navigate = useNavigate();

  const [data, setData] = useState<EquipmentType[]>([]);
  const [loading, setLoading] = useState(false);

  /* eslint-disable @typescript-eslint/no-use-before-define */
  useEffect(() => {
    const cacheResult = JSON.parse(
      sessionStorage.getItem(SessionStorageKey.EquipmentSearch) || '[]'
    ) as EquipmentType[];

    if (cacheResult.length > 0) {
      setData(cacheResult);
    } else {
      filter({ residuoEquipamento: '' });
    }
  }, []);

  const clear = () => {
    sessionStorage.removeItem(SessionStorageKey.EquipmentSearch);
    setData([]);
  };

  const filter = async (inputFilterData: FilterData) => {
    try {
      setLoading(true);
      const result = await equipmentsEquipment.search({
        residuoEquipamento: inputFilterData?.residuoEquipamento || null,
        limit: 1000,
        offset: 0,
      });
      sessionStorage.setItem(SessionStorageKey.EquipmentSearch, JSON.stringify(result));
      setData(result);
    } catch (error: any) {
      showNotification({
        title: 'Equipamentos',
        message: error?.isBusinessException ? error.description : 'Não foi possível pesquisar equipamentos.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmActionResult = async (item: EquipmentType, action: string, confirmed: boolean) => {
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      switch (action) {
        case 'excluir':
          await equipmentsEquipment.delete({ idResiduoEquipamento: item.idResiduoEquipamento });
          setData(data.filter((x) => x.idResiduoEquipamento !== item.idResiduoEquipamento));
          break;
        case 'visualizar':
          navigate(`/equipment/${item.idResiduoEquipamento}`, {
            state: {
              equipment: item,
            },
          });
          return;
        case 'editar':
          navigate(`/equipment/${item.idResiduoEquipamento}/edit`, {
            state: {
              equipment: item,
            },
          });
          return;
        default:
          break;
      }

      showNotification({
        title: 'Equipamentos',
        message: `Equipamento excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Equipamentos',
        message: error?.isBusinessException ? error.description : `Não foi possível ${action} o equipamento.`,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Equipment}
        title="Equipamentos"
        description="Gerencie equipamentos."
        buttons={[
          <Button
            key="Adicionar"
            color="primary"
            leftSection={<IconPlus size={18} />}
            disabled={loading}
            onClick={() => navigate('/equipment/add')}
            data-permission={Permission.EquipmentAdd}
          >
            Adicionar
          </Button>,
        ]}
      />
      <PageContent>
        <Filter filter={filter} clear={clear} loading={loading} />
        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading} />
          <Paper shadow="xs" p="md" withBorder>
            <DataView data={data} confirmActionResult={confirmActionResult} />
          </Paper>
        </div>
      </PageContent>
    </Card>
  );
}

export default EquipmentSearch;
